'use client';

import { motion } from 'motion/react';
import {
  Zap,
  CreditCard,
  Bitcoin,
  Shield,
  Gift,
  ArrowRight,
} from 'lucide-react';
import { useAppRouter } from '~/hooks';

export default function PricingPage() {
  const router = useAppRouter();

  const cryptoPayments = [
    {
      name: 'Bitcoin',
      icon: '₿',
      description: 'BTC, Lightning Network',
    },
    {
      name: 'Ethereum',
      icon: 'Ξ',
      description: 'ETH, ERC-20 tokens',
    },
    {
      name: 'Stablecoins',
      icon: '$',
      description: 'USDT, USDC, DAI',
    },
    {
      name: 'Altcoins',
      icon: '⟠',
      description: 'LTC, BCH, and more',
    },
  ];

  const howCreditsWork = [
    {
      step: 1,
      title: 'Purchase Credits',
      description:
        'Buy credit packages using crypto or traditional payment methods.',
    },
    {
      step: 2,
      title: 'Use Credits',
      description:
        'Each extraction job consumes credits based on data volume processed.',
    },
    {
      step: 3,
      title: 'Track Usage',
      description:
        'Monitor your credit balance in real-time from your dashboard.',
    },
    {
      step: 4,
      title: 'Top Up Anytime',
      description:
        'Add more credits whenever you need them—no subscriptions required.',
    },
  ];

  const creditCosts = [
    { service: 'Email Extraction', cost: '1 credit per email' },
    { service: 'Phone Number Extraction', cost: '1 credit per number' },
    { service: 'Social Media Profile', cost: '2 credits per profile' },
    { service: 'Bulk Processing (100+ items)', cost: '20% discount applied' },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="px-4 py-20 md:py-32 relative overflow-hidden bg-linear-to-br from-background-secondary via-secondary to-accent dark:from-[#1e293b] dark:via-[#312e81] dark:to-[#3730a3]">
        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-20 right-10 w-72 h-72 bg-linear-to-br from-primary/40 to-[#8b5cf6]/30 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 30, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-20 left-10 w-96 h-96 bg-linear-to-tr from-[#8b5cf6]/20 to-[#ec4899]/20 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -40, 0],
            y: [0, 40, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iY3VycmVudENvbG9yIiBzdHJva2Utb3BhY2l0eT0iMC4xIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30 text-primary dark:opacity-20"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-card/80 backdrop-blur-sm border border-card-border rounded-full mb-6 shadow-lg">
              <Zap className="w-5 h-5 text-primary" />
              <span className="text-primary">Flexible Credit System</span>
            </div>

            <h1 className="mb-6 text-foreground">
              Simple, Transparent Pricing
            </h1>
            <p className="text-foreground-secondary max-w-2xl mx-auto mb-8">
              No subscriptions, no hidden fees. Buy credits, use them when you
              need them, and pay with crypto for instant access and maximum
              privacy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Crypto Payments */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <Bitcoin className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="mb-4 text-foreground">Pay with Cryptocurrency</h2>
            <p className="text-foreground-secondary max-w-2xl mx-auto">
              Instant processing, enhanced privacy, and lower fees. We accept
              all major cryptocurrencies.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {cryptoPayments.map((crypto, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card border border-card-border rounded-xl p-6 text-center"
              >
                <div className="text-4xl mb-3">{crypto.icon}</div>
                <h3 className="mb-2 text-foreground">{crypto.name}</h3>
                <p className="text-foreground-secondary">
                  {crypto.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="bg-linear-to-br from-primary/10 to-[#8b5cf6]/10 border border-primary/20 rounded-2xl p-8">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-3">
                <Shield className="w-8 h-8 text-primary shrink-0" />
                <div>
                  <h3 className="mb-2 text-foreground">Secure & Private</h3>
                  <p className="text-foreground-secondary">
                    Non-custodial payments with industry-standard security
                    protocols.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Zap className="w-8 h-8 text-primary shrink-0" />
                <div>
                  <h3 className="mb-2 text-foreground">Instant Activation</h3>
                  <p className="text-foreground-secondary">
                    Credits are added to your account immediately after
                    confirmation.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Gift className="w-8 h-8 text-primary shrink-0" />
                <div>
                  <h3 className="mb-2 text-foreground">Bonus Credits</h3>
                  <p className="text-foreground-secondary">
                    Get 5% extra credits when paying with cryptocurrency.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Credits Work */}
      <section className="py-16 px-4 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="mb-4 text-foreground">How Credits Work</h2>
            <p className="text-foreground-secondary max-w-2xl mx-auto">
              Our credit system is simple and transparent. Pay only for what you
              use.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {howCreditsWork.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card border border-card-border rounded-xl p-6"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary mb-4">
                  {item.step}
                </div>
                <h3 className="mb-3 text-foreground">{item.title}</h3>
                <p className="text-foreground-secondary">{item.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Credit Costs Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-card border border-card-border rounded-2xl p-8"
          >
            <h3 className="mb-6 text-foreground">Credit Usage</h3>
            <div className="space-y-4">
              {creditCosts.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-secondary rounded-lg"
                >
                  <span className="text-foreground">{item.service}</span>
                  <span className="text-primary">{item.cost}</span>
                </div>
              ))}
            </div>
            <p className="text-foreground-secondary mt-6">
              * Exact credit costs may vary based on data complexity and volume.
              Check your dashboard for real-time pricing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="mb-4 text-foreground">Frequently Asked Questions</h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                q: 'Do credits expire?',
                a: 'Credits are valid for the period specified in your package (3-12 months). Unused credits expire after this period.',
              },
              {
                q: 'Can I get a refund?',
                a: 'Yes, unused credits can be refunded within 30 days of purchase. Contact our support team for assistance.',
              },
              {
                q: 'What cryptocurrencies do you accept?',
                a: 'We accept Bitcoin, Ethereum, USDT, USDC, DAI, Litecoin, Bitcoin Cash, and many other major cryptocurrencies.',
              },
              {
                q: 'Is there a monthly subscription?',
                a: 'No, Leadbunker uses a pay-as-you-go credit system. No recurring subscriptions or commitments required.',
              },
              {
                q: 'Can I buy more credits before mine run out?',
                a: 'Absolutely! You can purchase additional credits anytime, and they will be added to your account immediately.',
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card border border-card-border rounded-xl p-6"
              >
                <h3 className="mb-3 text-foreground">{faq.q}</h3>
                <p className="text-foreground-secondary">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-linear-to-br from-primary via-[#8b5cf6] to-[#ec4899] relative overflow-hidden">
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>

        {/* Floating Sketched Elements */}
        {/* Star sketch top-left */}
        <motion.svg
          className="absolute top-10 left-10 w-16 h-16 text-white/40"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 15, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M50 10 L55 40 L85 45 L60 65 L70 95 L50 80 L30 95 L40 65 L15 45 L45 40 Z" />
          <circle cx="50" cy="50" r="3" fill="currentColor" />
        </motion.svg>

        {/* Lightning bolt sketch top-right */}
        <motion.svg
          className="absolute top-20 right-20 w-20 h-20 text-white/50"
          animate={{
            y: [0, 15, 0],
            x: [0, -10, 0],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M55 10 L25 45 L45 48 L35 90 L75 45 L55 43 Z" />
        </motion.svg>

        {/* Checkmark circle sketch bottom-left */}
        <motion.svg
          className="absolute bottom-16 left-16 w-24 h-24 text-white/30"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, -10, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="40" strokeDasharray="5,5" />
          <path d="M30 50 L45 65 L70 35" strokeWidth="3" />
        </motion.svg>

        {/* Arrow sketch bottom-right */}
        <motion.svg
          className="absolute bottom-20 right-16 w-28 h-28 text-white/40"
          animate={{
            x: [0, 10, 0],
            y: [0, -10, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 50 Q50 10 90 50" />
          <path d="M70 30 L90 50 L70 70" />
          <circle cx="15" cy="48" r="3" fill="currentColor" />
        </motion.svg>

        {/* Sparkle sketch middle-left */}
        <motion.svg
          className="absolute top-1/2 left-1/4 w-12 h-12 text-white/60 hidden lg:block"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M50 10 L50 90 M10 50 L90 50" />
          <path d="M20 20 L80 80 M80 20 L20 80" />
        </motion.svg>

        {/* Scribble circle middle-right */}
        <motion.svg
          className="absolute top-1/3 right-1/4 w-20 h-20 text-white/35 hidden lg:block"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="30" strokeDasharray="3,3" />
          <circle cx="50" cy="50" r="20" strokeDasharray="2,4" />
          <circle cx="50" cy="50" r="10" strokeDasharray="1,2" />
        </motion.svg>

        {/* Dotted heart sketch */}
        <motion.svg
          className="absolute bottom-1/3 left-1/3 w-16 h-16 text-white/45 hidden md:block"
          animate={{
            y: [0, -15, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.8,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path
            d="M50 85 C50 85, 20 60, 20 40 C20 25, 30 15, 40 15 C45 15, 50 20, 50 20 C50 20, 55 15, 60 15 C70 15, 80 25, 80 40 C80 60, 50 85, 50 85 Z"
            strokeDasharray="4,4"
          />
        </motion.svg>

        {/* Plus signs */}
        <motion.svg
          className="absolute top-1/4 right-1/3 w-10 h-10 text-white/50 hidden lg:block"
          animate={{
            rotate: [0, 90, 0],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M50 20 L50 80 M20 50 L80 50" />
        </motion.svg>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="mb-4 text-white font-bold">Ready to Get Started?</h2>
            <p className="text-white/95 mb-8 max-w-2xl mx-auto">
              Purchase credits today and start extracting high-quality leads in
              minutes.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <motion.button
                onClick={() => router.push('/auth/signup')}
                className="px-8 py-4 bg-white text-primary rounded-lg hover:bg-white/95 transition-all shadow-xl hover:shadow-2xl flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <CreditCard className="w-5 h-5" />
                Buy Credits Now
              </motion.button>
              <motion.button
                onClick={() => router.push('/contact')}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <ArrowRight className="w-5 h-5" />
                Talk to Sales
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
